/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateCreator } from 'zustand';
import { createAppStore } from '@marriott/mi-store-utils';
import { produce } from 'immer';

/**
 * Call status type for tracking GraphQL query states
 */
export type HQVCallStatus = 'idle' | 'loading' | 'success' | 'error';

/**
 * Datalayer call status type
 */
export type DatalayerCallStatus = 'pending' | 'called';

/**
 * HQV Fallback mode type - determines how HQV should be displayed
 * - 'normal': All calls succeeded, display full HQV
 * - 'amenities_only': PropertyInfo and PhotoGallery failed, show only amenities
 * - 'closed': All calls failed, modal should be closed
 */
export type HQVFallbackMode = 'normal' | 'amenities_only' | 'closed';

/**
 * Interface for HQV Datalayer Tracking store state
 */
export interface HQVDatalayerTrackingState {
  propertyInfoCallStatus: HQVCallStatus;
  photogalleryCallStatus: HQVCallStatus;
  amenitiesCallStatus: HQVCallStatus;
  datalayerCallStatus: DatalayerCallStatus;
  retryAttempted: boolean;
  retryPropertyInfoStatus: HQVCallStatus;
  retryPhotogalleryStatus: HQVCallStatus;
  hqvFallbackMode: HQVFallbackMode;
  shouldTriggerRetry: boolean;
}

/**
 * Interface for HQV Datalayer Tracking store actions
 */
export interface HQVDatalayerTrackingActions {
  setPropertyInfoCallStatus: (status: HQVCallStatus) => void;
  setPhotogalleryCallStatus: (status: HQVCallStatus) => void;
  setAmenitiesCallStatus: (status: HQVCallStatus) => void;
  setDatalayerCallStatus: (status: DatalayerCallStatus) => void;
  setRetryAttempted: (attempted: boolean) => void;
  setRetryPropertyInfoStatus: (status: HQVCallStatus) => void;
  setRetryPhotogalleryStatus: (status: HQVCallStatus) => void;
  setHqvFallbackMode: (mode: HQVFallbackMode) => void;
  setShouldTriggerRetry: (shouldRetry: boolean) => void;
  resetStore: () => void;
}

/**
 * Combined store type
 */
export type HQVDatalayerTrackingStore = HQVDatalayerTrackingState & HQVDatalayerTrackingActions;

/**
 * Initial state for the store
 */
const initialState: HQVDatalayerTrackingState = {
  propertyInfoCallStatus: 'idle',
  photogalleryCallStatus: 'idle',
  amenitiesCallStatus: 'idle',
  datalayerCallStatus: 'pending',
  retryAttempted: false,
  retryPropertyInfoStatus: 'idle',
  retryPhotogalleryStatus: 'idle',
  hqvFallbackMode: 'normal',
  shouldTriggerRetry: false,
};

/**
 * HQV Datalayer Tracking Zustand store creator
 * Tracks the completion/failure status of phoenixShopHQVPropertyInfoCall, phoenixShopHQVPhotogalleryCall,
 * and phoenixShopHotelAmenities to conditionally trigger datalayer calls and manage retry/fallback logic
 */
const hqvDatalayerTrackingStoreCreator: StateCreator<HQVDatalayerTrackingStore> = set => {
  return {
    ...initialState,

    setPropertyInfoCallStatus: (status: HQVCallStatus) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.propertyInfoCallStatus = status;
        })
      );
    },

    setPhotogalleryCallStatus: (status: HQVCallStatus) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.photogalleryCallStatus = status;
        })
      );
    },

    setAmenitiesCallStatus: (status: HQVCallStatus) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.amenitiesCallStatus = status;
        })
      );
    },

    setDatalayerCallStatus: (status: DatalayerCallStatus) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.datalayerCallStatus = status;
        })
      );
    },

    setRetryAttempted: (attempted: boolean) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.retryAttempted = attempted;
        })
      );
    },

    setRetryPropertyInfoStatus: (status: HQVCallStatus) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.retryPropertyInfoStatus = status;
        })
      );
    },

    setRetryPhotogalleryStatus: (status: HQVCallStatus) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.retryPhotogalleryStatus = status;
        })
      );
    },

    setHqvFallbackMode: (mode: HQVFallbackMode) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.hqvFallbackMode = mode;
        })
      );
    },

    setShouldTriggerRetry: (shouldRetry: boolean) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.shouldTriggerRetry = shouldRetry;
        })
      );
    },

    resetStore: () => {
      set({ ...initialState });
    },
  };
};

/**
 * Create the HQV Datalayer Tracking store using the existing createAppStore pattern
 */
export const useHQVDatalayerTrackingStore = createAppStore(hqvDatalayerTrackingStoreCreator, {
  usePersistentStore: false,
});
